import { useEffect, useState } from 'react'
import { toast } from "react-toastify";
import ResolveCatchAllModal, { ResolveCatchAllSelection } from "../../components/ResolveCatchAllModal";

import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../app/store";
import { saveAs } from "file-saver";
import apiClient from "../../core/config/api";
import { usePhoneGate } from "../../components/phone/usePhoneGate";
import { downloadFileName } from "../../utils/fileNames";
import "./credit-logs.css";
import "./email_verify.css";

interface VerificationLogItem {
    id: number;
    label: string;
    email?: string | null;
    file_name?: string | null;
    created_at: string;
    status: string;
    result_status?: string | null;
    result_status_label?: string | null;
    data_count: number;
    valid_count?: number;
    invalid_count?: number;
    catch_all_count?: number;
    risky_all_count?: number;
    do_not_mail_count?: number;
    invalid_domain_count?: number;
    progress?: number | null;
    catch_all_resolve?: { submitted: number; resolved: number; status: "in_progress" | "completed" | "failed" } | null;
    catch_all_resolvable?: boolean;
    can_download_results: boolean;
    can_download_request_data: boolean;
    results?: any[];
}

const VerificationLogs = () => {
    // Free plan users verify their phone before downloading results.
    const { handlePhoneError, phoneModal } = usePhoneGate();
    const { } = useSelector((state: RootState) => state.auth);
    const [historyItems, setHistoryItems] = useState<VerificationLogItem[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [downloadingFiles, setDownloadingFiles] = useState<{ [key: string]: boolean }>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize] = useState(5);
    const [goToPageInput, setGoToPageInput] = useState("");

    const navigate = useNavigate();

    // Catch-All Resolver, same flow as the Email Verifier page.
    const [resolveTarget, setResolveTarget] = useState<{ id: number; count: number } | null>(null);
    const [resolvingIds, setResolvingIds] = useState<Record<number, boolean>>({});
    // Queued here but not yet reported in_progress by the server.
    const [resolveQueuedIds, setResolveQueuedIds] = useState<Set<number>>(new Set());

    const [aggregateTotals, setAggregateTotals] = useState({
        totalFiles: 0,
        totalVerified: 0,
        totalValid: 0,
        totalInvalid: 0,
        totalCatchAll: 0,
        totalRisky: 0,
        totalDoNotMail: 0,
        totalInvalidDomain: 0,
    });

    const fetchHistory = async (page: number = currentPage, silent = false) => {
        if (!silent) setHistoryLoading(true);
        setHistoryError(null);
        try {
            const response = await apiClient.get("/verifier-requests/re/history", {
                params: {
                    page,
                    page_size: pageSize,
                    search: searchTerm || undefined,
                },
            });

            const items = response?.data?.items || [];
            const mapped = items.map((item: any) => ({
                id: item.id,
                label: item.label,
                email: item.email,
                file_name: item.file_name,
                created_at: item.created_at,
                status: item.status,
                result_status: item.result_status,
                result_status_label: item.result_status_label,
                data_count: item.data_count,
                valid_count: item.valid_count,
                invalid_count: item.invalid_count,
                catch_all_count: item.catch_all_count,
                risky_all_count: item.risky_all_count,
                do_not_mail_count: item.do_not_mail_count,
                invalid_domain_count: item.invalid_domain_count,
                progress: item.progress,
                catch_all_resolve: item.catch_all_resolve ?? null,
                catch_all_resolvable: !!item.catch_all_resolvable,
                can_download_results: item.can_download_results,
                can_download_request_data: item.can_download_request_data,
                results: item.results,
            } as VerificationLogItem));

            const total = response?.data?.total ?? 0;
            const total_verified = response?.data?.total_verified ?? 0;
            const total_valid = response?.data?.total_valid ?? 0;
            const total_invalid = response?.data?.total_invalid ?? 0;
            const total_catch_all = response?.data?.total_catch_all ?? 0;
            const total_risky = response?.data?.total_risky ?? 0;
            const total_do_not_mail = response?.data?.total_do_not_mail ?? 0;
            const total_invalid_domain = response?.data?.total_invalid_domain ?? 0;

            setHistoryItems(mapped);
            setTotalPages(Math.max(1, Math.ceil(total / pageSize)));
            setTotalItems(total);
            setCurrentPage(page);
            setAggregateTotals({
                totalFiles: total,
                totalVerified: total_verified,
                totalValid: total_valid,
                totalInvalid: total_invalid,
                totalCatchAll: total_catch_all,
                totalRisky: total_risky,
                totalDoNotMail: total_do_not_mail,
                totalInvalidDomain: total_invalid_domain,
            });
        } catch (error: any) {
            // Only ever show a message the backend deliberately crafted for the
            // user - never fall back to error.message, which for a genuine
            // network failure is axios's raw internal string "Network Error"
            // and isn't something a user should see.
            setHistoryError(
                error?.response?.data?.message ||
                "Unable to load verification logs. Please check your connection and try again."
            );
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        fetchHistory(1);
    }, [searchTerm]);

    const isResolvingCatchAll = (item: VerificationLogItem) =>
        item.catch_all_resolve?.status === "in_progress" || resolveQueuedIds.has(item.id);

    // The resolver runs once per request; it is offered again only if the run failed.
    const canResolveAgain = (item: VerificationLogItem) =>
        !item.catch_all_resolve || item.catch_all_resolve.status === "failed";

    // While a resolve runs, refresh quietly so progress and the final results show.
    useEffect(() => {
        if (!historyItems.some(isResolvingCatchAll)) return;
        const timer = window.setInterval(() => fetchHistory(currentPage, true), 10000);
        return () => window.clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historyItems, resolveQueuedIds, currentPage]);

    // Drop the local "queued" mark once the server reports the run.
    useEffect(() => {
        if (resolveQueuedIds.size === 0) return;
        const reported = historyItems.filter((item) => resolveQueuedIds.has(item.id) && item.catch_all_resolve);
        if (!reported.length) return;
        setResolveQueuedIds((prev) => {
            const next = new Set(prev);
            reported.forEach((item) => next.delete(item.id));
            return next;
        });
    }, [historyItems, resolveQueuedIds]);

    const resolveCatchAll = async (id: number, selection?: ResolveCatchAllSelection) => {
        if (resolvingIds[id]) return;
        setResolvingIds((prev) => ({ ...prev, [id]: true }));
        try {
            await apiClient.patch(`/verifier-requests/re/${id}/resolve-catch-all`, selection ?? {});
            toast.success("Catch-All emails queued for resolution");
            setResolveQueuedIds((prev) => new Set(prev).add(id));
            setResolveTarget(null);
            await fetchHistory(currentPage, true);
        } catch (error: any) {
            const detail = error?.response?.data?.detail || error?.response?.data?.message;
            toast.error(typeof detail === "string" ? detail : "Failed to queue Catch-All resolution. Please try again.");
        } finally {
            setResolvingIds((prev) => ({ ...prev, [id]: false }));
        }
    };

    // Same In-Progress pill as the Email Verifier page, while the resolver runs.
    const renderResolveProgress = (item: VerificationLogItem) => {
        const resolve = item.catch_all_resolve;
        const percent = resolve?.status === "in_progress" && resolve.submitted > 0
            ? Math.min(99, Math.floor((resolve.resolved / resolve.submitted) * 100))
            : 0;
        return (
            <div className="bulk-resolving">
                <div className="bulk-status-pill bulk-status-resolving" aria-label={`In-Progress ${percent}%`}>
                    <div className="bulk-status-pill-row">
                        <span>In-Progress</span>
                        <span>{percent}%</span>
                    </div>
                    <div className="bulk-status-pill-track">
                        <div className="bulk-status-pill-fill" style={{ width: `${percent}%` }} />
                    </div>
                </div>
                <span className="bulk-resolving-label">
                    <svg className="spin" width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
                        <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
                    </svg>
                    Resolving
                    {resolve?.status === "in_progress" && resolve.submitted > 0 && (
                        <span className="bulk-resolving-count">
                            {formatNumber(resolve.resolved)}/{formatNumber(resolve.submitted)}
                        </span>
                    )}
                </span>
            </div>
        );
    };

    // Pending / In-Progress / Failed pill - the same one the Bulk Email
    // Verification tab shows while a file isn't finished.
    const renderStatusPill = (status: string, progress: number) => {
        if (status === "failed") {
            return (
                <div className="bulk-status-pill bulk-status-failed">
                    <div className="bulk-status-pill-row">
                        <span>Failed</span>
                        <button
                            type="button"
                            className="bulk-status-retry"
                            onClick={() => navigate("/email-verifier?tab=bulk")}
                            title="Upload the file again"
                        >
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
                                <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
                            </svg>
                            Retry
                        </button>
                    </div>
                    <div className="bulk-status-pill-track" />
                </div>
            );
        }
        let variant = "progress";
        let label = "In-Progress";
        const percent = Math.min(99, Math.max(0, Math.floor(progress)));
        if (status === "pending") {
            variant = "pending";
            label = "Pending";
        } else if (status === "paused") {
            variant = "pending";
            label = "Paused";
        }
        return (
            <div className={`bulk-status-pill bulk-status-${variant}`} aria-label={`${label} ${percent}%`}>
                <div className="bulk-status-pill-row">
                    <span>{label}</span>
                    <span>{percent}%</span>
                </div>
                <div className="bulk-status-pill-track">
                    <div className="bulk-status-pill-fill" style={{ width: `${percent}%` }} />
                </div>
            </div>
        );
    };

    const resolveLink = (item: VerificationLogItem, count: number) => (
        <button
            type="button"
            className="resolve-catch-all-link"
            onClick={() => setResolveTarget({ id: item.id, count })}
            disabled={!!resolvingIds[item.id]}
        >
            {resolvingIds[item.id] ? "Queuing..." : <>Resolve Catch-All <span aria-hidden="true">&rarr;</span></>}
        </button>
    );

    const handleSearch = () => {
        fetchHistory(1);
    };

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            fetchHistory(page);
        }
    };

    const handleGoToPage = () => {
        const page = parseInt(goToPageInput);
        if (!isNaN(page) && page >= 1 && page <= totalPages) {
            handlePageChange(page);
            setGoToPageInput(String(page));
        }
    };

    const renderPageNumbers = () => {
        const pages = [] as (number | string)[];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                pages.push(1, 2, 3, "...", totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, "...", currentPage, "...", totalPages);
            }
        }

        return pages;
    };

    const downloadResults = async (id: number) => {
        const stateKey = `results-${id}`;

        if (downloadingFiles[stateKey]) {
            return;
        }

        setDownloadingFiles(prev => ({ ...prev, [stateKey]: true }));
        try {
            const response = await apiClient.get(`/verifier-requests/re/${id}/download-results`, {
                responseType: "blob",
            });

            // Bulk rows always have a real uploaded file_name - keep naming
            // those downloads after it. Quick Verify rows (single email or a
            // combined batch) have no file_name, so use the server-provided
            // filename instead of falling back to the row's email/label - an
            // email address like "foo@bar.com" used as a filename previously
            // got its ".com" stripped by the old extension-removal regex,
            // leaving no extension at all and making the browser's save
            // dialog show it as an ambiguous file type.
            const item = historyItems.find((history) => history.id === id);
            // Always a clean ".csv" name - see utils/fileNames.
            const filename = downloadFileName(
                item?.file_name || response.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, ""),
                `verification_results_${id}`
            );

            saveAs(response.data, filename);
        } catch (error: any) {
            if (await handlePhoneError(error, () => downloadResults(id))) return;
            setHistoryError(
                error?.response?.data?.message ||
                "Failed to download verification results"
            );
        } finally {
            setDownloadingFiles(prev => ({ ...prev, [stateKey]: false }));
        }
    };

    const canDownloadResults = true;

    const totalFiles = aggregateTotals.totalFiles || totalItems;
    const totalVerified = aggregateTotals.totalVerified;
    const totalValid = aggregateTotals.totalValid;
    const totalInvalid = aggregateTotals.totalInvalid;
    const totalCatchAll = aggregateTotals.totalCatchAll;
    const totalRisky = aggregateTotals.totalRisky;
    const totalDoNotMail = aggregateTotals.totalDoNotMail;
    const totalInvalidDomain = aggregateTotals.totalInvalidDomain;

    const accuracy = 99.8;

    const getDisplayStatus = (item: VerificationLogItem) => {
        const raw = item.result_status || item.results?.[0]?.status || item.status || "";
        return raw.toString();
    };

    // The colour keying below still runs off the raw status; only the text
    // shown uses the backend's rendering of it.
    const getDisplayStatusLabel = (item: VerificationLogItem) =>
        item.result_status_label?.trim() || getDisplayStatus(item);

    // Badge colour from the raw status. Risky-Valid is shown as Valid, so it
    // gets the green Valid badge too (it was falling through to yellow).
    const getStatusClass = (status: string) => {
        const val = (status || "").trim().toLowerCase().replace(/[\s-]+/g, "_");
        if (["valid", "verified", "completed", "risky_valid"].includes(val)) return "valid-td";
        if (["invalid", "failed", "do_not_mail", "invalid_domain", "invalid_syntax"].includes(val)) return "invalid-td";
        if (["catch_all", "catchall"].includes(val)) return "catchall-td";
        if (val === "risky") return "risky-td";
        return "catch-all";
    };

    const getBulkProgress = (item: VerificationLogItem) => {
        if (item.status === "completed") return 100;
        if (item.status === "failed") return 0;
        if (typeof item.progress === "number") {
            const normalized = Math.max(0, Math.min(100, item.progress));
            return Math.trunc(normalized * 100) / 100;
        }
        return 0;
    };

    const getBulkCounts = (item: VerificationLogItem) => {
        const valid = Number(item.valid_count || 0);
        const invalid = Number(item.invalid_count || 0);
        const catchAll = Number(item.catch_all_count || 0);
        const risky = Number(item.risky_all_count || 0);
        return { valid, invalid, catchAll, risky };
    };

    const formatNumber = (value?: number | null) => {
        if (value === undefined || value === null || Number.isNaN(value)) return "0";
        return value.toLocaleString("en-US");
    };

    const formatDateTime = (dateStr: string) => {
        return new Date(dateStr).toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
        });
    };

    return (
        <>
            {phoneModal}
            {/* Title inside the left column, so the summary on the right starts
                at the top of the page instead of below the title. */}
            <div className="row">
                <div className="col-md-10">
                    <div className="dashboard-page">
                        <h1>Verification History</h1>
                        <p>View, download, and manage your email verification history.</p>
                    </div>
                    <div className="d-flex flex-row">
                        <div>
                            <img src="/images/verification.png" className="verification-img" alt="" />
                        </div>
                        <div>
                            <p className="user-list">Verification Records</p>
                        </div>
                    </div>

                    <div className="user-management-box">
                        <div className="d-flex flex-row justify-content-between">
                            <div className="search-wrapper">
                                <img
                                    src="/images/search-icon.png"
                                    alt="search"
                                    className="search-icon left"
                                />
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="Search by email or file name..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                />
                            </div>

                            <div>
                                {historyError && (
                                    <p className="bulk-upload-error-text">
                                        {historyError}
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className="table-wrapper verification-logs-table-wrapper">
                            <table className="custom-table">
                                <thead>
                                    <tr>
                                        <th>Email ID / File Name</th>
                                        <th>Verified On</th>
                                        <th>Total Records</th>
                                        <th>Results</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {historyLoading ? (
                                        <tr>
                                            <td colSpan={5} style={{ padding: "24px" }}>
                                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                                                    <div className="spinner-border" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ) : historyItems.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="text-center">No verification history found.</td>
                                        </tr>
                                    ) : (
                                        historyItems.map((item) => {
                                            const isBulk = item.data_count !== 1;
                                            const status = (item.status || "").toLowerCase();
                                            const pendingStatuses = new Set([
                                                "pending",
                                                "processing",
                                                "paused",
                                                "retrying",
                                                "restarted",
                                                "in_progress",
                                                "inprogress",
                                            ]);
                                            const bulkIncomplete = isBulk && (pendingStatuses.has(status) || getBulkProgress(item) < 100);
                                            const downloadDisabled = (
                                                !item.can_download_results ||
                                                !canDownloadResults ||
                                                downloadingFiles[`results-${item.id}`] ||
                                                bulkIncomplete
                                            );

                                            return (
                                                <tr key={item.id}>
                                                    <td>
                                                        <div className="d-flex flex-row">
                                                            <div className="max">
                                                                <img
                                                                    src={item.data_count === 1 ? "/images/var-email.png" : "/images/var-page.png"}
                                                                    alt=""
                                                                />
                                                            </div>
                                                            <div
                                                                className="max"
                                                                title={item.label}
                                                                style={{
                                                                    maxWidth: 200,
                                                                    display: "inline-block",
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                    whiteSpace: "nowrap",
                                                                    verticalAlign: "bottom",
                                                                    cursor: "pointer",
                                                                }}
                                                            >
                                                                {item.label}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{formatDateTime(item.created_at)}</td>
                                                    <td>{formatNumber(item.data_count)}</td>
                                                    <td>
                                                        {isBulk ? (
                                                            (() => {
                                                                const bulkProgress = getBulkProgress(item);

                                                                if (status === "failed" || status === "pending" || status === "paused")
                                                                    return renderStatusPill(status, bulkProgress);
                                                                if (bulkProgress >= 100 && isResolvingCatchAll(item)) return renderResolveProgress(item);
                                                                if (bulkProgress >= 100) {
                                                                    // Same six outcomes and icons as the Email Verifier and
                                                                    // Credit Logs pages; each count shows on hover.
                                                                    const counts = getBulkCounts(item);
                                                                    const resolvable = counts.catchAll;
                                                                    const outcomes = [
                                                                        { label: "Valid", icon: "validate.svg", count: counts.valid, tone: "valid" },
                                                                        { label: "Invalid", icon: "invalid.svg", count: counts.invalid, tone: "invalid" },
                                                                        { label: "Catch-All", icon: "catch-all.svg", count: counts.catchAll, tone: "catchall" },
                                                                        { label: "Do Not Mail", icon: "do-not-sent.svg", count: Number(item.do_not_mail_count || 0), tone: "catch" },
                                                                        { label: "Invalid Domain", icon: "invalid-domain.svg", count: Number(item.invalid_domain_count || 0), tone: "invalid" },
                                                                        { label: "Risky", icon: "risk.svg", count: counts.risky, tone: "risky" },
                                                                    ];
                                                                    return (
                                                                        <div className="verification-results-cell">
                                                                        <div className="d-flex flex-row credit-logs-details">
                                                                            {outcomes.map((outcome) => (
                                                                                <div
                                                                                    key={outcome.label}
                                                                                    className="icon-tooltip"
                                                                                    tabIndex={0}
                                                                                    aria-label={`${outcome.label}: ${formatNumber(outcome.count)}`}
                                                                                >
                                                                                    <img className="credit-logs-outcome-icon" src={`/images/${outcome.icon}`} alt="" />
                                                                                    <span className={`tooltip-text tooltip-${outcome.tone}`}>
                                                                                        {outcome.label}: {formatNumber(outcome.count)}
                                                                                    </span>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                        {resolvable > 0 && canResolveAgain(item) && resolveLink(item, resolvable)}
                                                                        </div>
                                                                    );
                                                                }

                                                                return renderStatusPill(status, bulkProgress);
                                                            })()
                                                        ) : (
                                                            isResolvingCatchAll(item) ? (
                                                                renderResolveProgress(item)
                                                            ) : (
                                                                <div className="verification-results-cell">
                                                                    <span className={getStatusClass(getDisplayStatus(item))}>
                                                                        {getDisplayStatusLabel(item)}
                                                                    </span>
                                                                </div>
                                                            )
                                                        )}
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="add-users"
                                                            disabled={downloadDisabled}
                                                            style={{ cursor: downloadDisabled ? "not-allowed" : "pointer" }}
                                                            onClick={() => downloadResults(item.id)}
                                                        >
                                                            {downloadingFiles[`results-${item.id}`] ? (
                                                                <span className="spinner-border spinner-border-sm" role="status">
                                                                    <span className="visually-hidden">Downloading...</span>
                                                                </span>
                                                            ) : (
                                                                "Download CSV"
                                                            )}
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                            {totalPages > 1 && (
                                <div className="pagination-container">
                                    <button
                                        className="pagination-arrow"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1 || historyLoading}
                                    >
                                        &lt;
                                    </button>

                                    {renderPageNumbers().map((page, index) => (
                                        page === "..." ? (
                                            <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                                        ) : (
                                            <button
                                                key={page}
                                                className={`pagination-number ${currentPage === page ? "active" : ""}`}
                                                onClick={() => handlePageChange(page as number)}
                                                disabled={historyLoading}
                                            >
                                                {page}
                                            </button>
                                        )
                                    ))}
                                    <button
                                        className="pagination-arrow"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages || historyLoading}
                                    >
                                        &gt;
                                    </button>

                                    <div className="go-to-page">
                                        <span>Go to page</span>
                                        <input
                                            type="number"
                                            min={1}
                                            max={totalPages}
                                            value={goToPageInput}
                                            onChange={(e) => setGoToPageInput(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === "Enter") {
                                                    handleGoToPage();
                                                }
                                            }}
                                            onBlur={handleGoToPage}
                                            className="page-input"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-md-2">

                    <div className="user-management-box-1 verification-logs-summary">
                        <p className="total-credits">
                            Total Files
                        </p>
                        <span className="total-credits">{formatNumber(totalFiles)}</span>

                        <div className="devider"></div>
                        <p className="total-credits">
                            Emails Verified
                        </p>
                        <span className="total-credits-r">{formatNumber(totalVerified)}</span>

                        <div className="devider"></div>

                        <p className="total-credits">
                            Valid
                        </p>
                        <span className="total-credits-m">{formatNumber(totalValid)}</span>

                        <div className="devider"></div>
                        <p className="total-credits">
                            Catch-All
                        </p>
                        <span className="total-credits-y">{formatNumber(totalCatchAll)}</span>

                        <div className="devider"></div>
                        <p className="total-credits">
                            Risky
                        </p>
                        <span className="total-credits">{formatNumber(totalRisky)}</span>

                        {/* Counted on their own, not as Invalid. */}
                        <div className="devider"></div>
                        <p className="total-credits">
                            Do Not Mail
                        </p>
                        <span className="total-credits summary-do-not-mail">{formatNumber(totalDoNotMail)}</span>

                        <div className="devider"></div>
                        <p className="total-credits">
                            Invalid Domain
                        </p>
                        <span className="total-credits summary-invalid-domain">{formatNumber(totalInvalidDomain)}</span>

                        <div className="devider"></div>
                        <p className="total-credits">
                            Invalid
                        </p>
                        <span className="total-credits-t">{formatNumber(totalInvalid)}</span>

                        <div className="devider"></div>
                        <p className="total-credits">
                            Accuracy Rate
                        </p>
                        <span className="total-credits">{`${formatNumber(accuracy)}%`}</span>

                    </div>
                </div>
            </div>
            {resolveTarget && (
                <ResolveCatchAllModal
                    requestId={resolveTarget.id}
                    emailCount={resolveTarget.count}
                    submitting={!!resolvingIds[resolveTarget.id]}
                    onClose={() => setResolveTarget(null)}
                    onConfirm={(selection) => resolveCatchAll(resolveTarget.id, selection)}
                />
            )}
        </>
    )
}

export default VerificationLogs;